<script setup lang="ts">
type TransportIcon = "car" | "bus" | "train" | "plane" | "taxi" | "key-round";

interface TransportOption {
  label: string;
  icon: TransportIcon;
  description: string;
  price_hint?: string;
  cta_label?: string;
  cta_href?: string;
}

interface TransportBlock {
  title: string;
  intro?: string;
  options: TransportOption[];
}

defineProps<{
  block: TransportBlock;
}>();

const { t } = useI18n();

const iconMap: Record<TransportIcon, string> = {
  car: "tabler:car",
  bus: "tabler:bus",
  train: "tabler:train",
  plane: "tabler:plane",
  taxi: "tabler:brand-uber",
  "key-round": "tabler:key",
};
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-lime-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
    aria-labelledby="transport-block-heading"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-amber-400/10 blur-3xl dark:bg-amber-400/10"
    />

    <div class="relative mx-auto w-full max-w-7xl px-4 py-8 md:py-12">
      <header data-reveal class="mb-6 max-w-3xl md:mb-8">
        <div
          class="flex items-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500"
        >
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
          <span>{{ t("location.transport.eyebrow") }}</span>
        </div>
        <h2
          id="transport-block-heading"
          class="mt-4 font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.title }}
        </h2>
        <p
          v-if="block.intro"
          class="mt-5 font-body text-base text-slate-600 md:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <ul data-reveal class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3">
        <li
          v-for="(option, i) in block.options"
          :key="i"
          class="group relative flex flex-col gap-4 rounded-2xl border border-slate-200/70 bg-white p-6 transition-all duration-300 hover:border-amber-400/60 hover:shadow-lg hover:shadow-amber-400/5 md:p-7 dark:border-slate-700/60 dark:bg-slate-900/60 dark:hover:border-amber-400/40"
        >
          <div class="flex items-start gap-4">
            <span
              class="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-900/5 text-slate-900 ring-1 ring-slate-900/5 transition-colors group-hover:bg-amber-400/10 group-hover:text-amber-600 dark:bg-white/5 dark:text-white dark:ring-white/10 dark:group-hover:text-amber-300"
            >
              <Icon :name="iconMap[option.icon]" size="24" aria-hidden="true" />
            </span>
            <div class="flex-1 min-w-0">
              <h3
                class="font-display text-xl italic leading-tight text-slate-900 dark:text-white"
              >
                {{ option.label }}
              </h3>
              <p
                v-if="option.price_hint"
                class="mt-1 font-body text-xs uppercase tracking-[0.2em] text-amber-600 dark:text-amber-400"
              >
                {{ option.price_hint }}
              </p>
            </div>
          </div>

          <p
            class="font-body text-sm leading-relaxed text-slate-600 dark:text-slate-300"
          >
            {{ option.description }}
          </p>

          <a
            v-if="option.cta_href"
            :href="option.cta_href"
            class="mt-auto inline-flex items-center gap-1.5 self-start font-body text-xs uppercase tracking-[0.2em] text-slate-700 transition-colors hover:text-amber-600 dark:text-slate-200 dark:hover:text-amber-400"
          >
            {{ option.cta_label || t("location.transport.default_cta") }}
            <span aria-hidden="true" class="transition-transform group-hover:translate-x-1">→</span>
          </a>
        </li>
      </ul>
    </div>
  </section>
</template>
